// Fetches the Collective Mock Draft from a published Google Sheet (TSV).
// Override the URL with REACT_APP_MOCK_DRAFT_TSV_URL when needed.
const TSV_URL =
  process.env.REACT_APP_MOCK_DRAFT_TSV_URL ||
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vTT5bca0ah3hoXjUoukB58F9WoEbiDZ39tjEt3wNwqyxJNmlswzeGVtMg1TQZZTEEJYpDfSeQnTOVyZ/pub?output=tsv";

export interface ChatMessage {
  author: string;
  text: string;
}

export interface MockPick {
  pick: string; // A
  team: string; // B
  picker: string; // C
  player: string; // D
  reasoning: string; // E
  responses: ChatMessage[]; // F, G (+ H picker reply)
  position: string; // I
  height: string; // J
  wingspan: string; // K
  weight: string; // L
  teamCollege: string; // M
  experience: string; // N
  age: string; // O
  profileLink: string; // P
}

// Minimal RFC-ish delimited parser that handles quoted fields with embedded
// delimiters, newlines and doubled quotes. A quote only opens a quoted field
// when it appears at the START of a field (matching how Google exports). A
// quote anywhere else is treated as a literal character, so a stray '"' inside
// a cell (e.g. an inch mark like 6'2") can't swallow the rest of the sheet.
function parseDelimited(text: string, delimiter = "\t"): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let atFieldStart = true;
  let i = 0;

  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"' && atFieldStart) {
      inQuotes = true;
      atFieldStart = false;
      i++;
      continue;
    }
    if (ch === delimiter) {
      row.push(field);
      field = "";
      atFieldStart = true;
      i++;
      continue;
    }
    if (ch === "\r") {
      i++;
      continue;
    }
    if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      atFieldStart = true;
      i++;
      continue;
    }
    field += ch;
    atFieldStart = false;
    i++;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

// Responses are authored as "[name]: content". Falls back to a raw message.
function parseResponse(raw: string, fallbackAuthor = ""): ChatMessage | null {
  const s = (raw || "").trim();
  if (!s) return null;
  const m = s.match(/^\[(.+?)\]\s*:?\s*([\s\S]*)$/);
  if (m) return { author: m[1].trim(), text: m[2].trim() };
  return { author: fallbackAuthor, text: s };
}

export async function fetchMockDraft(): Promise<MockPick[]> {
  const res = await fetch(TSV_URL);
  if (!res.ok) throw new Error(`Sheet fetch failed: ${res.status}`);
  const text = await res.text();
  const rows = parseDelimited(text);
  if (rows.length < 2) return [];

  const col = (r: string[], i: number) => (r[i] || "").trim();

  return rows
    .slice(1) // drop header row
    .filter((r) => col(r, 0) !== "" || col(r, 3) !== "") // has a pick # or player
    .map((r) => {
      const picker = col(r, 2);
      const responses = [
        parseResponse(col(r, 5)),
        parseResponse(col(r, 6)),
        parseResponse(col(r, 7), picker), // picker's optional reply
      ].filter((x): x is ChatMessage => x !== null);

      return {
        pick: col(r, 0),
        team: col(r, 1),
        picker,
        player: col(r, 3),
        reasoning: col(r, 4),
        responses,
        position: col(r, 8),
        height: col(r, 9),
        wingspan: col(r, 10),
        weight: col(r, 11),
        teamCollege: col(r, 12),
        experience: col(r, 13),
        age: col(r, 14),
        profileLink: col(r, 15),
      };
    });
}
